/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/*
 * The editorial panel: a note from the map's authors, and who they are.
 *
 * The backend serves it as data. The markup is built here, because it is
 * a design surface — someone laying out the panel should not have to open
 * the backend to move a heading.
 *
 * Everything the map's editor typed is set as text, never as markup, with
 * one exception: `contentHtml` is a rich-text field they authored, and it
 * is inserted as HTML. That is the same trust boundary the template this
 * replaces already drew with `|safe`, and it is drawn in exactly one
 * place so it stays visible.
 */

import { t } from './i18n.js';

/**
 * Build one `<section>` with a `<main>` inside it.
 *
 * @param {string} [className] - Class for the section, if any.
 * @returns {{section: HTMLElement, main: HTMLElement}} Both elements.
 */
function section(className) {
    const element = document.createElement('section');
    if (className) element.className = className;
    const main = document.createElement('main');
    element.appendChild(main);
    return { section: element, main };
}

/**
 * Render the editorial into an element.
 *
 * @param {HTMLElement} root - The panel to fill.
 * @param {object} data - The editorial, as the endpoint serves it.
 */
export function renderEditorial(root, data) {
    const parts = document.createDocumentFragment();

    const close = document.createElement('div');
    close.className = 'close';
    const closeLink = document.createElement('a');
    closeLink.href = '#close';
    closeLink.className = 'fas fa-times';
    const closeName = document.createElement('span');
    closeName.className = 'sr-only';
    closeName.textContent = t('editorial.close');
    closeLink.appendChild(closeName);
    close.appendChild(closeLink);
    parts.appendChild(close);

    const title = section();
    const heading = document.createElement('h2');
    heading.textContent = data.title || t('editorial.defaultTitle');
    title.main.appendChild(heading);
    parts.appendChild(title.section);

    // The section is emitted even without an image: the panel is a stack
    // of sections and the stylesheet spaces them, so dropping one shifts
    // everything below it.
    const image = section();
    const imageParagraph = document.createElement('p');
    if (data.imageUrl) {
        const img = document.createElement('img');
        img.src = data.imageUrl;
        // Decorative: the editorial carries no alternative text for this
        // image and the heading above already names the section. An empty
        // alt is the correct answer, an invented one is not.
        img.alt = '';
        imageParagraph.appendChild(img);
    }
    image.main.appendChild(imageParagraph);
    parts.appendChild(image.section);

    const content = section();
    if (data.contentHtml) {
        content.main.innerHTML = data.contentHtml;
    }
    parts.appendChild(content.section);

    if (data.teamMembers?.length) {
        const team = section('team');
        const teamHeading = document.createElement('h3');
        teamHeading.textContent = t('editorial.team');
        team.main.appendChild(teamHeading);
        data.teamMembers.forEach((member) => {
            const figure = document.createElement('figure');
            const portrait = document.createElement('div');
            portrait.className = 'portrait';
            const img = document.createElement('img');
            img.src = member.imageUrl || '';
            // Empty alt on purpose: the caption below carries the name
            // and role, so describing the portrait would only repeat it.
            img.alt = '';
            portrait.appendChild(img);
            const caption = document.createElement('figcaption');
            const name = document.createElement('strong');
            name.textContent = member.name || '';
            caption.appendChild(name);
            caption.appendChild(
                document.createTextNode(` ${member.description || ''}`)
            );
            figure.append(portrait, caption);
            team.main.appendChild(figure);
        });
        parts.appendChild(team.section);
    }

    root.replaceChildren(parts);
}

/**
 * Fetch the editorial and render it.
 *
 * @param {HTMLElement} editorialElem - The panel to fill.
 * @param {string} editorialUrl - Where to fetch it from.
 * @returns {Promise<boolean>} Whether a panel was rendered.
 */
export const initEditorial = (editorialElem, editorialUrl) => {
    const el = editorialElem;
    if (!el) return Promise.resolve(false);

    return fetch(editorialUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'default',
        headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
    })
        .then((res) => {
            // A map without an editorial answers 404, which is an answer
            // rather than a failure: there is simply no panel to build.
            if (res.status === 404) return null;
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
        })
        .then((data) => {
            if (!data) return false;
            renderEditorial(el, data);
            return true;
        })
        .catch(() => {
            console.error('Failed to load editorial.');
            return false;
        });
};
